// src/pages/billing/BillingProductDetailPage.tsx

import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { observer } from '@react';
import { useBillingPrice, useBillingProduct, useI18n } from '@react/hooks';
import {
  Button,
  Input,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Alert,
  Badge,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  ErrorAlert,
} from '@react/components';
import { Select } from '@/components/Select';

function formatMoney(money: any): string {
  if (!money) return '--';
  const amount = (money.amount ?? 0) / Math.pow(10, money.decimal_places ?? 2);
  return amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatDate(dateStr: string | undefined | null): string {
  if (!dateStr) return '--';
  try { return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }); }
  catch { return String(dateStr); }
}

function enumLabel(value: string, prefix: string): string {
  return (value || '').replace(prefix, '').replace(/_/g, ' ');
}

export const BillingProductDetailPage = observer(() => {
  const { id } = useParams<{ id: string }>();
  const store = useBillingProduct();
  const priceStore = useBillingPrice();
  const { t } = useI18n();
  const [prices, setPrices] = useState<any[]>([]);
  const [showPriceForm, setShowPriceForm] = useState(false);
  const [priceForm, setPriceForm] = useState({ model: 'PRICING_MODEL_FLAT', currency: 'CURRENCY_USD', billing_period: 'BILLING_PERIOD_MONTHLY', unit_amount: '' });
  const [priceSubmitting, setPriceSubmitting] = useState(false);
  const item = store.currentItem as any;

  const loadPrices = (productId: string) => {
    priceStore.ListPrices().then(() => {
      const items = priceStore.items as any[];
      setPrices(Array.isArray(items) ? items.filter((p: any) => (p.product?.id ?? p.product_id) === productId) : []);
    }).catch(() => {});
  };

  useEffect(() => {
    if (id) {
      store.GetBillingProduct(id);
      loadPrices(id);
    }
    return () => { store.clearCurrent(); };
  }, [id]);

  const handleCreatePrice = async () => {
    if (!id) return;
    setPriceSubmitting(true);
    try {
      await priceStore.CreatePrice(id, {
        model: priceForm.model,
        currency: priceForm.currency,
        billing_period: priceForm.billing_period,
        unit_amount: { amount: Math.round(parseFloat(priceForm.unit_amount || '0') * 100), currency: priceForm.currency, decimal_places: 2 },
      } as any);
      loadPrices(id);
      setShowPriceForm(false);
      setPriceForm({ model: 'PRICING_MODEL_FLAT', currency: 'CURRENCY_USD', billing_period: 'BILLING_PERIOD_MONTHLY', unit_amount: '' });
    } catch {
      // Error handled by store
    } finally {
      setPriceSubmitting(false);
    }
  };

  if (store.isLoading) {
    return <div className="flex items-center justify-center h-64"><div className="text-muted-foreground">{t('common.loading')}</div></div>;
  }
  if (store.error) {
    return <><ErrorAlert error={store.error} /><Link to="/billing/products" className="mt-2 inline-block text-sm underline text-primary mt-2 inline-block text-sm underline text-primary">{t("common.backToList")}</Link></>;
  }
  if (!item) {
    return <Alert variant="warning" title={t('common.notFound')}><Link to="/billing/products" className="mt-2 inline-block text-sm underline text-primary">{t('common.backToList')}</Link></Alert>;
  }

  return (
    <div className="space-y-6">
      <nav className="text-sm">
        <Link to="/billing" className="text-muted-foreground hover:text-foreground">Billing</Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <Link to="/billing/products" className="text-muted-foreground hover:text-foreground">Products</Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <span className="text-foreground">{String(item.name ?? id)}</span>
      </nav>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-foreground">{String(item.name ?? '')}</h1>
            {item.is_active ? <Badge variant="success" size="sm">Active</Badge> : <Badge variant="secondary" size="sm">Inactive</Badge>}
          </div>
          <p className="text-sm text-muted-foreground mt-1">{String(item.description ?? '')}</p>
        </div>
        <Link to={`/billing/products/${id}/edit`}><Button variant="secondary">{t('common.edit')}</Button></Link>
      </div>

      <Card>
        <CardHeader><CardTitle>Product Info</CardTitle></CardHeader>
        <CardContent>
          <dl className="divide-y divide-border">
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Unit</dt>
              <dd className="text-sm text-foreground col-span-2">{String(item.unit ?? '--')}</dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Unit Label</dt>
              <dd className="text-sm text-foreground col-span-2">{String(item.unit_label ?? '--')}</dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Created</dt>
              <dd className="text-sm text-foreground col-span-2">{formatDate(item.created_at)}</dd>
            </div>
          </dl>
        </CardContent>
      </Card>

      {/* Prices */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Prices</CardTitle>
            <Button variant="secondary" size="sm" onClick={() => setShowPriceForm(!showPriceForm)}>
              {showPriceForm ? 'Cancel' : 'Add Price'}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {showPriceForm && (
            <div className="mb-4 p-4 border border-border rounded-lg space-y-3">
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Model</label>
                  <Select
                    value={priceForm.model}
                    onChange={(e) => setPriceForm((p) => ({ ...p, model: e.target.value }))} className="w-full">
                    <option value="PRICING_MODEL_FLAT">Flat</option>
                    <option value="PRICING_MODEL_PER_UNIT">Per Unit</option>
                    <option value="PRICING_MODEL_TIERED">Tiered</option>
                    <option value="PRICING_MODEL_GRADUATED">Graduated</option>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Period</label>
                  <Select
                    value={priceForm.billing_period}
                    onChange={(e) => setPriceForm((p) => ({ ...p, billing_period: e.target.value }))} className="w-full">
                    <option value="BILLING_PERIOD_MONTHLY">Monthly</option>
                    <option value="BILLING_PERIOD_QUARTERLY">Quarterly</option>
                    <option value="BILLING_PERIOD_ANNUAL">Annual</option>
                    <option value="BILLING_PERIOD_WEEKLY">Weekly</option>
                  </Select>
                </div>
                <Input label="Unit Amount" type="text" value={priceForm.unit_amount} onChange={(e) => setPriceForm((p) => ({ ...p, unit_amount: e.target.value }))} placeholder="9.99" />
              </div>
              <Button onClick={handleCreatePrice} disabled={priceSubmitting}>
                {priceSubmitting ? 'Creating...' : 'Create Price'}
              </Button>
            </div>
          )}
          {prices.length > 0 ? (
            <Table striped hoverable>
              <TableHeader>
                <TableRow>
                  <TableHead>Model</TableHead>
                  <TableHead>Period</TableHead>
                  <TableHead>Currency</TableHead>
                  <TableHead>Unit Amount</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">{t('common.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {prices.map((price: any, i: number) => (
                  <TableRow key={price.entity?.id || i}>
                    <TableCell>{enumLabel(price.model, 'PRICING_MODEL_')}</TableCell>
                    <TableCell>{enumLabel(price.billing_period, 'BILLING_PERIOD_')}</TableCell>
                    <TableCell>{enumLabel(price.currency, 'CURRENCY_')}</TableCell>
                    <TableCell>{formatMoney(price.unit_amount)}</TableCell>
                    <TableCell>{price.is_active ? <Badge variant="success" size="sm">Active</Badge> : <Badge variant="secondary" size="sm">Inactive</Badge>}</TableCell>
                    <TableCell className="text-right">
                      <Link to={`/billing/prices/${price.entity?.id}`}><Button variant="ghost" size="sm">View</Button></Link>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <p className="text-sm text-muted-foreground">No prices configured</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
});

export default BillingProductDetailPage;
